import React from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, Position } from 'reactflow';

interface CustomEdgeProps extends EdgeProps {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  style?: React.CSSProperties;
  markerEnd?: any;
  data?: {
    direction?: 'up' | 'down' | 'left' | 'right';
    intensity?: number;
    label?: string;
    animated?: boolean; // Controls dashed line animation
    animationProgress?: number; // 0 to 1, how much of the edge is filled with gold
    showGoldOverlay?: boolean; // Controls whether to show gold fill animation
  };
}

const CustomEdge: React.FC<CustomEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data, // We'll pass our custom yOffset here
}) => {
  // Get direction, intensity, and animation from data, with sensible defaults
  const direction = data?.direction || 'down';
  const intensity = data?.intensity || 150;
  const animated = data?.animated || false;
  const animationProgress = data?.animationProgress ?? 0;
  const showGoldOverlay = data?.showGoldOverlay || false;

  // Calculate the distance between source and target
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Calculate control points for a very rounded bezier curve
  let controlPoint1X, controlPoint1Y, controlPoint2X, controlPoint2Y;

  // Base control points at 30% of the distance for rounder curves
  controlPoint1X = sourceX + (dx * 0.3);
  controlPoint2X = targetX - (dx * 0.3);
  controlPoint1Y = sourceY + (dy * 0.3);
  controlPoint2Y = targetY - (dy * 0.3);

  // Apply offsets depending on direction - treat left/right like up/down
  if (direction === 'up' || direction === 'down') {
    const yOffset = direction === 'up' ? -intensity : intensity;
    controlPoint1Y += yOffset;
    controlPoint2Y += yOffset;
  } else if (direction === 'left' || direction === 'right') {
    const xOffset = direction === 'left' ? -intensity : intensity;
    controlPoint1X += xOffset;
    controlPoint2X += xOffset;
  }

  // Generate a smooth bezier curve path
  const edgePath = `M ${sourceX} ${sourceY} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${targetX} ${targetY}`;

  // Gold animation gradient - align with edge direction
  const gradientId = `gradient-${id}`;
  const glowId = `glow-${id}`;
  const baseColor = style?.stroke || '#000000';
  const goldColor = '#FFD700';
  
  // Calculate gradient direction based on source and target positions
  const gradientX1 = sourceX;
  const gradientY1 = sourceY;
  const gradientX2 = targetX;
  const gradientY2 = targetY;

  return (
    <>
      <defs>
        <linearGradient 
          id={gradientId} 
          x1={gradientX1} 
          y1={gradientY1} 
          x2={gradientX2} 
          y2={gradientY2}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={goldColor} stopOpacity="1" />
          <stop offset={`${animationProgress * 100}%`} stopColor={goldColor} stopOpacity="1" />
          <stop offset={`${animationProgress * 100}%`} stopColor="transparent" stopOpacity="0" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </linearGradient>
        
        {/* Glow filter for gold arrow */}
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Base edge with original styling and dashed animation */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeDasharray: animated ? '5,5' : undefined,
          animation: animated ? 'dashdraw 0.3s linear infinite' : undefined,
        }}
      />
      
      {/* Gold overlay edge that fills up */}
      {showGoldOverlay && (
        <BaseEdge
          id={`${id}-gold`}
          path={edgePath}
          markerEnd={
            markerEnd && typeof markerEnd === 'object'
              ? {
                  ...markerEnd,
                  color: animationProgress >= 0.95 ? goldColor : 'transparent',
                }
              : undefined
          }
          style={{
            stroke: `url(#${gradientId})`,
            strokeWidth: Number(style?.strokeWidth ?? 2) + 1,
            fill: 'none',
            pointerEvents: 'none',
            filter: animationProgress > 0 ? `url(#${glowId})` : 'none',
          }}
        />
      )}
    </>
  );
};

export default CustomEdge;